import { useState } from 'react';
import Slider from 'react-slick';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import Typography from '@alltrails/shared/denali/components/Typography';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import classNames from 'classnames';
import { wrapUrlSafe } from '@alltrails/shared/utils/languageSupport';
import Button from '@alltrails/shared/denali/components/Button';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import useUser from 'hooks/useUser';
import FeatureSlide from './FeatureSlide';
import PRO_FEATURE_CAROUSEL_STRINGS from './plusFeatureCarousel.strings';
import controlImageData from './imageData';
import variantImageData from './variantImageData';
import * as styles from './styles/styles.module.scss';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

export default function PlusFeatureCarousel({ showVariant = false }: { showVariant?: boolean }) {
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const [slideIndex, setSlideIndex] = useState(0);

  const slickSettings = {
    afterChange: (index: number) => {
      setSlideIndex(index);
    },
    arrows: false,
    infinite: false,
    slidesToShow: 3
  };

  const mobileSlickSettings = {
    ...slickSettings,
    slidesToShow: 1,
    centerMode: true,
    centerPadding: '30px'
  };

  const {
    formattedDefaultMessages: {
      DISTANCE_AWAY_TITLE,
      DISTANCE_AWAY_DESCRIPTION,
      DISTANCE_AWAY_ALT,
      MAP_DOWNLOAD_TITLE,
      MAP_DOWNLOAD_DESCRIPTION,
      MAP_DOWNLOAD_ALT,
      OFF_ROUTE_TITLE,
      OFF_ROUTE_DESCRIPTION,
      OFF_ROUTE_ALT
    }
  } = useFormatMessage(PRO_FEATURE_CAROUSEL_STRINGS);

  const imageData = showVariant ? variantImageData : controlImageData;

  return (
    <div className={styles.container}>
      <Typography variant="heading600" component="h2" className={styles.header}>
        <FormattedMessage defaultMessage="Make the great outdoors even greater" />
      </Typography>
      {/* Mobile slider */}
      <Slider {...mobileSlickSettings} className={classNames(styles.sliderContainer, styles.mobileSliderContainer)}>
        <FeatureSlide
          title={DISTANCE_AWAY_TITLE}
          description={DISTANCE_AWAY_DESCRIPTION}
          images={imageData.distanceAwayImages}
          altText={DISTANCE_AWAY_ALT}
          isLeftSlide
        />
        <FeatureSlide
          title={MAP_DOWNLOAD_TITLE}
          description={MAP_DOWNLOAD_DESCRIPTION}
          altText={MAP_DOWNLOAD_ALT}
          images={imageData.downloadMapsImages}
          isMiddleSlide
        />
        <FeatureSlide
          title={OFF_ROUTE_TITLE}
          description={OFF_ROUTE_DESCRIPTION}
          images={imageData.offRouteAlertImages}
          altText={OFF_ROUTE_ALT}
          isRightSlide
        />
      </Slider>
      {/* Desktop slider */}
      <Slider {...slickSettings} className={styles.sliderContainer}>
        <FeatureSlide
          title={DISTANCE_AWAY_TITLE}
          description={DISTANCE_AWAY_DESCRIPTION}
          images={imageData.distanceAwayImages}
          altText={DISTANCE_AWAY_ALT}
          isLeftSlide
        />
        <FeatureSlide
          title={MAP_DOWNLOAD_TITLE}
          description={MAP_DOWNLOAD_DESCRIPTION}
          altText={MAP_DOWNLOAD_ALT}
          images={imageData.downloadMapsImages}
          isMiddleSlide
        />
        <FeatureSlide
          title={OFF_ROUTE_TITLE}
          description={OFF_ROUTE_DESCRIPTION}
          images={imageData.offRouteAlertImages}
          altText={OFF_ROUTE_ALT}
          isRightSlide
        />
      </Slider>
      <div className={styles.dotContainer}>
        <div className={classNames({ [styles.active]: slideIndex === 0, [styles.dot]: true })} />
        <div className={classNames({ [styles.active]: slideIndex === 1, [styles.dot]: true })} />
        <div className={classNames({ [styles.active]: slideIndex === 2, [styles.dot]: true })} />
      </div>
      {showVariant && (
        <div className={styles.buttonContainer}>
          <Button
            text={<FormattedMessage defaultMessage="Start your free trial" />}
            linkInfo={{
              href: user ? wrapUrlSafe('/plus/checkout', languageRegionCode) : wrapUrlSafe('/signup?returnTo=%2Fplus%2Fcheckout', languageRegionCode)
            }}
            testId="carousel-try-plus"
            variant="primary"
          />
        </div>
      )}
    </div>
  );
}
